import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { on } from "@ember/modifier";
import { ajax } from "discourse/lib/ajax";
import eq from "truth-helpers/helpers/eq";
import not from "truth-helpers/helpers/not";

export default class ProxyviewsCompare extends Component {
  @tracked slugA = "";
  @tracked slugB = "";
  @tracked result = null;
  @tracked loading = false;
  @tracked error = null;

  constructor() {
    super(...arguments);
    // Read URL query params so the directory's "Compare Now" button
    // (?ids=slug1,slug2) pre-fills and auto-compares. Previously the
    // compare page ignored URL params entirely, so users landing from
    // the directory had to re-select both providers manually.
    const params = new URLSearchParams(window.location.search);
    const ids = params.get("ids");
    if (ids) {
      const [a, b] = ids.split(",").map((s) => s.trim()).filter(Boolean);
      if (a && b) {
        this.slugA = a;
        this.slugB = b;
        // Retry until providers are loaded (route fetches async)
        let attempts = 0;
        const tryCompare = () => {
          attempts++;
          if ((this.args.providers || []).length > 0 || attempts > 20) {
            this.compare();
          } else {
            setTimeout(tryCompare, 100);
          }
        };
        setTimeout(tryCompare, 100);
      }
    }
  }

  get canCompare() {
    return this.slugA && this.slugB && this.slugA !== this.slugB && !this.loading;
  }

  @action setA(e) {
    this.slugA = e.target.value;
  }
  @action setB(e) {
    this.slugB = e.target.value;
  }

  @action handleLogoError(e) {
    e.target.style.display = "none";
  }

  @action async compare() {
    if (!this.slugA || !this.slugB || this.slugA === this.slugB) {
      this.error = "Pick two different providers.";
      return;
    }
    this.loading = true;
    this.error = null;
    try {
      const data = await ajax(
        `/providers/compare.json?slug_a=${this.slugA}&slug_b=${this.slugB}`
      );
      this.result = data;
    } catch (e) {
      this.error = "Could not load comparison.";
    } finally {
      this.loading = false;
    }
  }

  <template>
    <section class="pd-profile">
      <div class="pd-hero">
        <span class="pd-hero__eyebrow">Head to Head</span>
        <h1 class="pd-hero__title">Compare <span>providers</span></h1>
        <p class="pd-hero__subtitle">Pick any two providers for a side-by-side breakdown.</p>
      </div>

      <div class="pd-compare__picker">
        <select class="pd-toolbar__select" {{on "change" this.setA}}>
          <option value="">Provider A…</option>
          {{#each @providers as |p|}}
            <option value={{p.slug}} selected={{eq this.slugA p.slug}}>{{p.name}}</option>
          {{/each}}
        </select>
        <span class="pd-compare__vs">VS</span>
        <select class="pd-toolbar__select" {{on "change" this.setB}}>
          <option value="">Provider B…</option>
          {{#each @providers as |p|}}
            <option value={{p.slug}} selected={{eq this.slugB p.slug}}>{{p.name}}</option>
          {{/each}}
        </select>
        <button
          type="button"
          class="pd-profile__cta-button"
          disabled={{not this.canCompare}}
          {{on "click" this.compare}}
        >
          {{if this.loading "Comparing…" "Compare →"}}
        </button>
      </div>

      {{#if this.error}}<p class="pd-compare__error">{{this.error}}</p>{{/if}}

      {{#if this.result}}
        <div class="pd-compare__table-wrap">
          <div class="pd-compare__table">
            <div class="pd-compare__row pd-compare__row--head">
              <div class="pd-compare__cell pd-compare__cell--label">Metric</div>
              <div class="pd-compare__cell">
                {{#if this.result.provider_a.logo_url}}
                  <img src={{this.result.provider_a.logo_url}} alt={{this.result.provider_a.name}} class="pd-compare__logo" loading="lazy" {{on "error" this.handleLogoError}} />
                {{/if}}
                {{this.result.provider_a.name}}
              </div>
              <div class="pd-compare__cell">
                {{#if this.result.provider_b.logo_url}}
                  <img src={{this.result.provider_b.logo_url}} alt={{this.result.provider_b.name}} class="pd-compare__logo" loading="lazy" {{on "error" this.handleLogoError}} />
                {{/if}}
                {{this.result.provider_b.name}}
              </div>
            </div>
            {{#each this.result.comparison as |row|}}
              <div class="pd-compare__row">
                <div class="pd-compare__cell pd-compare__cell--label">{{row.label}}</div>
                <div class="pd-compare__cell {{if (eq row.winner 'a') 'pd-compare__cell--win'}}">
                  {{if row.provider_a row.provider_a "—"}}
                  {{#if (eq row.winner "a")}}<span class="pd-compare__crown">★</span>{{/if}}
                </div>
                <div class="pd-compare__cell {{if (eq row.winner 'b') 'pd-compare__cell--win'}}">
                  {{if row.provider_b row.provider_b "—"}}
                  {{#if (eq row.winner "b")}}<span class="pd-compare__crown">★</span>{{/if}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      {{else if this.loading}}
        <div class="pd-compare__loading">Loading comparison…</div>
      {{else}}
        <div class="pd-compare__empty">
          <p>Select two providers above and click Compare to see a detailed head-to-head breakdown.</p>
        </div>
      {{/if}}
    </section>
  </template>
}
